import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

type ShopRef = { id: string; name?: string };

type AuthState = {
  user: any | null;
  role?: string | null;
  shops: ShopRef[];
  accessToken?: string | null;
};

type AuthContextValue = {
  auth: AuthState;
  setAuthFromLogin: (loginData: any) => void;
  clearAuth: () => void;
  isAdmin: () => boolean;
  ownsShop: (shopId?: string) => boolean;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const decodeJwt = (token?: string | null) => {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const b = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(atob(b).split('').map((c) => '%'+('00'+c.charCodeAt(0).toString(16)).slice(-2)).join(''));
    return JSON.parse(json);
  } catch (e) {
    console.error('Failed to decode JWT', e);
    return null;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [auth, setAuth] = useState<AuthState>({ user: null, role: null, shops: [], accessToken: null });

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    let role = user?.role || null;
    let shops: ShopRef[] = [];

    if (user && user.shop) {
      // login responses sometimes include `shop` for single-shop owners
      shops = [{ id: user.shop.id || user.shop.shop_id || user.shop._id, name: user.shop.name }].filter(s => s.id);
    }

    if (!role && token) {
      const claims = decodeJwt(token);
      if (claims) {
        role = claims.role || role || null;
        // Accept shops claim as array of ids or objects
        if (!shops.length && claims.shops) {
          try {
            shops = Array.isArray(claims.shops)
              ? claims.shops.map((s: any) => (typeof s === 'string' ? { id: s } : { id: s.id || s.shop_id, name: s.name }))
              : [];
          } catch (e) {
            console.warn('Unable to parse shops from token claims', e);
          }
        }
      }
    }

    setAuth({ user, role, shops, accessToken: token });
  }, []);

  const setAuthFromLogin = (loginData: any) => {
    const tokens = loginData?.tokens;
    const accessToken = tokens?.access?.token || localStorage.getItem('accessToken');
    const user = loginData?.user || null;
    const shop = loginData?.shop || null;

    let shops: ShopRef[] = [];
    if (shop) {
      const id = shop.id || shop.shop_id || (shop._id as any);
      if (id) shops.push({ id, name: shop.name });
    }

    // decode token for role / shops if present
    const claims = decodeJwt(accessToken);
    const role = user?.role || claims?.role || null;
    if (!shops.length && claims?.shops) {
      shops = Array.isArray(claims.shops)
        ? claims.shops.map((s: any) => (typeof s === 'string' ? { id: s } : { id: s.id || s.shop_id, name: s.name }))
        : [];
    }

    localStorage.setItem('accessToken', accessToken || '');
    if (tokens?.refresh?.token) localStorage.setItem('refreshToken', tokens.refresh.token);
    if (user) localStorage.setItem('user', JSON.stringify(user));

    setAuth({ user, role, shops, accessToken: accessToken || null });
  };

  const clearAuth = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setAuth({ user: null, role: null, shops: [], accessToken: null });
  };

  const isAdmin = () => String(auth.role || '').toUpperCase() === 'ADMIN' || auth.user?.isAdmin || false;
  const ownsShop = (shopId?: string) => {
    if (!shopId) return false;
    if (isAdmin()) return true;
    return auth.shops.some(s => s.id === shopId || s.id === String(shopId));
  };

  const value = useMemo(() => ({ auth, setAuthFromLogin, clearAuth, isAdmin, ownsShop }), [auth]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export default AuthContext;
